import React, { useState } from "react";
import WidgetCard from "./WidgetCard";
import { Col, Row, Table, Input } from "antd";
import { chain, each, flatten, keys, map, filter } from "underscore";
import {
  CompetitiveProductsDaywiseTrendsMetric,
  ICompetitiveProductsDaywiseTrendsProps,
} from "@ts/common-types";
import { IoSearch } from "react-icons/io5";

const CompetitiveProductsDaywiseTrends: React.FC<ICompetitiveProductsDaywiseTrendsProps> = ({ title, widgetData }) => {
  const [searchValue, setSearchValue] = useState("");

  const getDayColumns = () => {
    const daysSet = chain(widgetData)
      .pluck("metrics")
      .flatten()
      .reduce(
        (acc: Set<string>, metric: CompetitiveProductsDaywiseTrendsMetric) => {
          const dayKeys = keys(metric).filter((key) => key !== "type");
          each(dayKeys, (dayKey) => acc.add(dayKey));
          return acc;
        },
        new Set<string>()
      )
      .value();

    const days = Array.from(daysSet);
    return map(days, (day) => ({
      title: day,
      dataIndex: day,
      key: day,
      width: 115,
    }));
  };

  const baseColumns = [
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      fixed: "left",
      width: 500,
      render: (text: string | null, record: any, index: number) => ({
        children: text,
        props: {
          rowSpan: record.rowSpan,
        },
      }),
    },
    {
      title: "",
      dataIndex: "metric",
      key: "metric",
      fixed: "left",
      width: 115,
      render: (text: string) => <strong>{text}</strong>,
    },
  ];

  const dynamicDayColumns = getDayColumns();
  const columns = [...baseColumns, ...dynamicDayColumns];

  const transformData = () => {
    const filteredWidgetData = searchValue
      ? filter(widgetData, (product) =>
          product.name.toLowerCase().includes(searchValue.toLowerCase())
        )
      : widgetData;

    return flatten(
      map(filteredWidgetData, (product) => {
        const { name, metrics } = product;
        return map(metrics, (metric, index) => {
          const row: Record<string, string | number | null> = {
            key: `${name}-${metric.type}`,
            product_name: index === 0 ? name : null, // Note: Using SKU as the product name
            metric: metric.type,
            rowSpan: index === 0 ? metrics.length : 0,
          };

          each(dynamicDayColumns, (col) => {
            row[col.dataIndex] = metric[col.dataIndex] || "-";
          });
          return row;
        });
      })
    );
  };

  return (
    <WidgetCard className="my-5">
      <Row className="fs-5 fw-bold mb-3" align="middle">
        <Col span={19}>
          <h5 className="d-inline-block me-3">
            {title || "Daywise Trends of Competitive Products"}
          </h5>
        </Col>
        <Col span={5}>
        <Input
            placeholder="Search Product Name/ASIN"
            style={{width: 240}}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            prefix={<IoSearch className="fs-6" style={{ color: "#C7C7C7" }} />}
            allowClear
          />
          </Col>
      </Row>

      <Row className="d-flex w-100">
        <Table
          columns={columns}
          dataSource={transformData()}
          pagination={false}
          bordered={false}
          rowClassName={() => "ant-table-row"}
          style={{ textAlign: "center" }}
          scroll={{ y: 400, x: 1500 }}
        />
      </Row>
    </WidgetCard>
  );
};

export default CompetitiveProductsDaywiseTrends;